import { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { getToken, apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradeModal } from '@/components/UpgradeModal';
import { MoneyRain } from '@/components/MoneyRain';
import { playCelebration } from '@/utils/audio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Progress Step Component
function SetupStep({ number, title, isComplete, isActive, children }) {
  return (
    <div className={`relative ${isActive ? 'opacity-100' : 'opacity-60'}`}>
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          isComplete ? 'bg-green-500 text-white' : isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          {isComplete ? '✓' : number}
        </div>
        <div className="flex-1">
          <h3 className={`font-semibold ${isComplete ? 'text-green-700' : isActive ? 'text-gray-900' : 'text-gray-500'}`}>
            {title}
          </h3>
          {isActive && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </div>
  );
}

// Paycheck Card Component (replaces "template")
function PaycheckCard({ paycheck, onEdit, onDelete, onToggle }) {
  return (
    <div 
      className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
        paycheck.is_active 
          ? 'bg-white border-green-200 shadow-sm' 
          : 'bg-gray-50 border-gray-200'
      }`}
      role="listitem"
      aria-label={`${paycheck.name} paycheck, ${paycheck.is_active ? 'active' : 'paused'}, $${paycheck.default_amount.toLocaleString()}`}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={() => onToggle(paycheck)}
          className={`w-12 h-7 rounded-full transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-500 ${
            paycheck.is_active ? 'bg-green-500' : 'bg-gray-300'
          }`}
          role="switch"
          aria-checked={paycheck.is_active}
          aria-label={`${paycheck.is_active ? 'Disable' : 'Enable'} ${paycheck.name} paycheck`}
        >
          <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform shadow ${
            paycheck.is_active ? 'translate-x-6' : 'translate-x-1'
          }`} aria-hidden="true" />
        </button>
        <div>
          <p className={`font-semibold ${paycheck.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
            {paycheck.name}
          </p>
          <p className={`text-sm ${paycheck.is_active ? 'text-gray-500' : 'text-gray-400'}`}>
            {paycheck.is_active ? 'Active' : 'Paused'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-xl font-bold ${paycheck.is_active ? 'text-green-600' : 'text-gray-400'}`}>
          ${paycheck.default_amount.toLocaleString()}
        </span>
        <button
          onClick={() => onEdit(paycheck)}
          className="p-2 text-gray-400 hover:text-blue-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          aria-label={`Edit ${paycheck.name} paycheck`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          onClick={() => onDelete(paycheck.id)}
          className="p-2 text-gray-400 hover:text-red-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
          aria-label={`Delete ${paycheck.name} paycheck`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Income Item Component
function IncomeItem({ income, onDelete, isLocked }) {
  return (
    <div className={`flex items-center justify-between py-3 px-4 rounded-lg ${
      income.from_template ? 'bg-blue-50' : 'bg-gray-50'
    }`}>
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{income.name}</span>
        {income.from_template && (
          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
            recurring
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="font-bold text-green-600">${income.amount.toLocaleString()}</span>
        {!isLocked && (
          <button
            onClick={() => onDelete(income.id)}
            className="text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Remove"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// Period Card Component
function PeriodCard({ 
  period, 
  index, 
  onAddIncome, 
  onDeleteIncome, 
  onApplyPaychecks, 
  activePaychecksCount,
  saving,
  newIncome,
  onIncomeChange,
  payFrequency
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const totalIncome = period.income || 0;
  const hasIncome = period.income_sources?.length > 0;
  
  const getPeriodLabel = () => {
    switch (payFrequency) {
      case 'daily': return 'Day';
      case 'weekly': return 'Week';
      case 'monthly': return 'Month';
      default: return 'Pay Period';
    }
  };

  const handleAdd = () => {
    if (newIncome.name && newIncome.amount) {
      onAddIncome(index, period.period_start_iso);
      setShowAddForm(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-5">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-emerald-100 text-sm font-medium">{getPeriodLabel()} #{period.period}</p>
            <h3 className="text-xl font-bold mt-1">{period.date} — {period.end_date}</h3>
          </div>
          <div className="text-right">
            <p className="text-emerald-100 text-sm">Total Income</p>
            <p className="text-2xl font-bold">${totalIncome.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex gap-2">
          {activePaychecksCount > 0 && (
            <button
              onClick={() => onApplyPaychecks(period.period_start_iso)}
              disabled={saving}
              className="flex-1 py-2.5 px-4 bg-blue-50 text-blue-700 rounded-xl font-medium hover:bg-blue-100 transition-colors text-sm disabled:opacity-50"
            >
              ⚡ Auto-fill from Paychecks
            </button>
          )}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="py-2.5 px-4 bg-gray-50 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors text-sm"
          >
            + Add Income
          </button>
        </div>
      </div>

      {/* Add Income Form */}
      {showAddForm && (
        <div className="p-4 bg-gray-50 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-700 mb-3">Add one-time income</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Income Source *
              </label>
              <input
                type="text"
                value={newIncome.name}
                onChange={(e) => onIncomeChange(index, 'name', e.target.value)}
                placeholder="What's this income from?"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid={`income-name-${index}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount ($) *
              </label>
              <input
                type="number"
                value={newIncome.amount}
                onChange={(e) => onIncomeChange(index, 'amount', e.target.value)}
                placeholder="0.00"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                data-testid={`income-amount-${index}`}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleAdd}
                disabled={saving || !newIncome.name || !newIncome.amount}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`add-income-btn-${index}`}
              >
                Add Income
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Income List */}
      <div className="p-4">
        {hasIncome ? (
          <div className="space-y-2">
            {period.income_sources.map((source) => (
              <IncomeItem
                key={source.id}
                income={source}
                onDelete={onDeleteIncome}
                isLocked={false}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">💵</div>
            <p className="text-gray-500 text-sm">No income added yet</p>
            <p className="text-gray-400 text-xs mt-1">
              {activePaychecksCount > 0 
                ? 'Click "Auto-fill" to add your regular paychecks'
                : 'Add your income sources above'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Income() {
  const { toast } = useToast();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const [payDate, setPayDate] = useState(null);
  const [payFrequency, setPayFrequency] = useState('biweekly');
  const [forceThree, setForceThree] = useState(false);
  const [payPeriods, setPayPeriods] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [closingMonth, setClosingMonth] = useState(false);
  const [showPaycheckForm, setShowPaycheckForm] = useState(false);
  const [editingPaycheck, setEditingPaycheck] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  // 1099 Status
  const [is1099, setIs1099] = useState(false);
  const [loading1099, setLoading1099] = useState(true);
  
  // Upgrade Modal for premium features
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Month Summary Modal state
  const [showMonthSummary, setShowMonthSummary] = useState(false);
  const [monthSummary, setMonthSummary] = useState(null);
  
  // Money Rain celebration effect
  const [showMoneyRain, setShowMoneyRain] = useState(false);
  
  // Update Bills/Debts prompt after wrap-up
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  
  const settingsModified = useRef(false);

  const [newIncome, setNewIncome] = useState({
    0: { name: '', amount: '' },
    1: { name: '', amount: '' },
    2: { name: '', amount: '' },
  });

  const [newPaycheck, setNewPaycheck] = useState({ name: '', default_amount: '' });

  // Load 1099 status
  useEffect(() => {
    const load1099Status = async () => {
      try {
        const token = getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API}/1099/status`, {
          credentials: 'include',
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          setIs1099(data.is_1099 || false);
        }
      } catch (error) {
        console.error('Error loading 1099 status:', error);
      } finally {
        setLoading1099(false);
      }
    };
    load1099Status();
  }, []);

  // Toggle 1099 status - Premium feature
  const toggle1099Status = async () => {
    // Gate for premium users - only allow enabling if premium
    if (!is1099 && !isPremium()) {
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      const token = getToken();
      const newValue = !is1099;
      const response = await fetch(`${API}/1099/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ is_1099: newValue }),
      });
      
      if (response.ok) {
        setIs1099(newValue);
        toast({
          title: newValue ? '1099 Mode Enabled' : '1099 Mode Disabled',
          description: newValue 
            ? 'Your income will be tracked for tax estimation when you wrap up each month.'
            : '1099 income tracking has been turned off.',
          variant: newValue ? 'success' : 'default'
        });
      }
    } catch (error) {
      console.error('Error updating 1099 status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update 1099 status',
        variant: 'destructive'
      });
    }
  };

  // Load user settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${API}/settings`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.pay_date) {
            setPayDate(new Date(data.pay_date));
          } else {
            setPayDate(new Date());
          }
          setPayFrequency(data.pay_frequency || 'biweekly');
          setForceThree(data.force_three || false);
        } else {
          setPayDate(new Date());
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setPayDate(new Date());
      } finally {
        setSettingsLoaded(true);
      }
    };

    loadSettings();
  }, []);

  // Save settings when they change
  useEffect(() => {
    if (!settingsLoaded || !settingsModified.current) return;

    const saveSettings = async () => {
      setSavingSettings(true);
      try {
        await fetch(`${API}/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            pay_date: payDate?.toISOString(),
            pay_frequency: payFrequency,
            force_three: forceThree,
          }),
        });
      } catch (error) {
        console.error('Error saving settings:', error);
      } finally {
        setSavingSettings(false);
      }
    };

    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [payDate, payFrequency, forceThree, settingsLoaded]);

  const loadBudget = useCallback(async () => {
    if (!payDate) return;
    
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      const response = await fetch(`${API}/budget/calculate`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          pay_date: payDate.toISOString(),
          force_three: forceThree,
          pay_frequency: payFrequency,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setPayPeriods(data);
      }
    } catch (error) {
      console.error('Error loading budget:', error);
    } finally {
      setLoading(false);
    }
  }, [payDate, forceThree, payFrequency]);

  const loadTemplates = useCallback(async () => {
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/income/templates`, {
        credentials: 'include',
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, []);

  useEffect(() => {
    if (settingsLoaded && payDate) {
      loadBudget();
    }
    loadTemplates();
  }, [loadBudget, loadTemplates, settingsLoaded, payDate]);

  // Determine current setup step
  useEffect(() => {
    if (templates.length === 0) {
      setCurrentStep(1);
    } else if (payPeriods.every(p => !p.income_sources?.length)) {
      setCurrentStep(2);
    } else {
      setCurrentStep(3);
    }
  }, [templates, payPeriods]);

  const handlePayDateChange = (newDate) => {
    settingsModified.current = true;
    setPayDate(newDate);
  };

  const handlePayFrequencyChange = (newFrequency) => {
    settingsModified.current = true;
    setPayFrequency(newFrequency);
  };

  const handleForceThreeChange = (newValue) => {
    settingsModified.current = true;
    setForceThree(newValue);
  };

  const addIncomeSource = async (periodIndex, periodStartIso) => {
    const incomeData = newIncome[periodIndex];
    if (!incomeData?.name || !incomeData?.amount) return;

    setSaving(true);
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      const response = await fetch(`${API}/income`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          pay_period_start: periodStartIso,
          name: incomeData.name,
          amount: parseFloat(incomeData.amount),
        }),
      });

      if (response.ok) {
        // 🎉 Trigger money rain and play celebration sound
        setShowMoneyRain(true);
        playCelebration();
        
        setNewIncome((prev) => ({
          ...prev,
          [periodIndex]: { name: '', amount: '' },
        }));
        loadBudget();
        
        toast({
          title: '💰 Income Added!',
          description: `$${parseFloat(incomeData.amount).toLocaleString()} from ${incomeData.name}`,
          variant: 'success'
        });
      }
    } catch (error) {
      console.error('Error adding income:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteIncomeSource = async (incomeId) => {
    setSaving(true);
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/income/${incomeId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        await loadBudget();
      }
    } catch (error) {
      console.error('Error deleting income:', error);
    } finally {
      setSaving(false);
    }
  };

  const applyPaychecks = async (periodStartIso) => {
    setSaving(true);
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      const response = await fetch(`${API}/income/apply-templates`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          pay_period_start: periodStartIso,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.created > 0) {
          // 🎉 Trigger money rain when paychecks are applied
          setShowMoneyRain(true);
          playCelebration();
          loadBudget();
          
          toast({
            title: '💵 Paychecks Applied!',
            description: `${data.created} paycheck${data.created > 1 ? 's' : ''} added to this period`,
            variant: 'success'
          });
        }
      }
    } catch (error) {
      console.error('Error applying paychecks:', error);
    } finally {
      setSaving(false);
    }
  };

  const wrapUpMonth = async () => {
    if (!window.confirm(
      '✨ Ready to wrap up this month?\n\n' +
      '• Your budget will be saved to History\n' +
      '• Income & expenses will be cleared for a fresh start\n' +
      '• Bills reset to unpaid for the new month\n' +
      '• Your paycheck templates stay intact\n\n' +
      'This helps you start each month clean!'
    )) return;

    setClosingMonth(true);
    try {
      const response = await apiPost('/budget/close-month', {
        pay_date: payDate.toISOString(),
        pay_frequency: payFrequency,
      });

      if (response.ok) {
        const data = await response.json();
        // Show the Month Summary modal instead of alert
        setMonthSummary({
          monthName: data.saved_as,
          ...data.summary
        });
        setShowMonthSummary(true);
        // After showing summary, we'll prompt to update bills/debts
        setShowUpdatePrompt(true);
        loadBudget();
      }
    } catch (error) {
      console.error('Error wrapping up month:', error);
    } finally {
      setClosingMonth(false);
    }
  };

  // Paycheck (Template) CRUD
  const savePaycheck = async () => {
    if (!newPaycheck.name || !newPaycheck.default_amount) return;

    setSaving(true);
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      const url = editingPaycheck
        ? `${API}/income/templates/${editingPaycheck.id}`
        : `${API}/income/templates`;
      
      const response = await fetch(url, {
        method: editingPaycheck ? 'PUT' : 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          name: newPaycheck.name,
          default_amount: parseFloat(newPaycheck.default_amount),
        }),
      });

      if (response.ok) {
        setNewPaycheck({ name: '', default_amount: '' });
        setEditingPaycheck(null);
        setShowPaycheckForm(false);
        loadTemplates();
      }
    } catch (error) {
      console.error('Error saving paycheck:', error);
    } finally {
      setSaving(false);
    }
  };

  const editPaycheck = (paycheck) => {
    setEditingPaycheck(paycheck);
    setNewPaycheck({
      name: paycheck.name,
      default_amount: paycheck.default_amount.toString(),
    });
    setShowPaycheckForm(true);
  };

  const deletePaycheck = async (paycheckId) => {
    setSaving(true);
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/income/templates/${paycheckId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        await loadTemplates();
      }
    } catch (error) {
      console.error('Error deleting paycheck:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePaycheckActive = async (paycheck) => {
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
      
      await fetch(`${API}/income/templates/${paycheck.id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_active: !paycheck.is_active }),
      });
      loadTemplates();
    } catch (error) {
      console.error('Error toggling paycheck:', error);
    }
  };

  const handleNewIncomeChange = (periodIndex, field, value) => {
    setNewIncome((prev) => ({
      ...prev,
      [periodIndex]: { ...prev[periodIndex], [field]: value },
    }));
  };

  if (loading || !settingsLoaded || !payDate) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your income...</p>
        </div>
      </div>
    );
  }

  const activePaychecks = templates.filter((t) => t.is_active);
  const totalActivePaychecks = activePaychecks.reduce((sum, p) => sum + p.default_amount, 0);
  const hasAnyIncome = payPeriods.some(p => p.income_sources?.length > 0);

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="income-title">
            💵 Income Setup
          </h1>
          <p className="text-gray-500 mt-1">
            Set up your paychecks once, then auto-fill each pay period
          </p>
        </div>
        
        {hasAnyIncome && (
          <button
            onClick={wrapUpMonth}
            disabled={closingMonth}
            data-testid="close-month-button"
            className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-medium hover:from-violet-600 hover:to-purple-700 transition-all shadow-sm disabled:opacity-50"
          >
            {closingMonth ? 'Saving...' : '✨ Wrap Up & Start Fresh'}
          </button>
        )}
      </div>

      {/* 1099 Self-Employment Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl flex-shrink-0">
            📋
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-amber-900">1099 / Self-Employed?</h3>
              {/* 1099 Toggle */}
              <button
                onClick={toggle1099Status}
                disabled={loading1099}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  is1099 ? 'bg-amber-500' : 'bg-gray-300'
                }`}
                data-testid="toggle-1099"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    is1099 ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-amber-700 mb-3">
              {is1099 
                ? '✅ Your income will be archived for tax tracking when you wrap up each month.'
                : 'Enable to track your 1099 income for tax estimation purposes.'}
            </p>
            <Link 
              to="/app/tax-calculator"
              className="inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:text-amber-900"
              data-testid="tax-calculator-link"
            >
              Open Tax Calculator →
            </Link>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-semibold text-gray-900">Setup Progress</h2>
          <span className="text-sm text-gray-500">
            {currentStep === 3 ? '✓ Complete' : `Step ${currentStep} of 3`}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex-1 flex items-center">
              <div className={`h-2 flex-1 rounded-full ${
                step <= currentStep ? 'bg-green-500' : 'bg-gray-200'
              }`} />
            </div>
          ))}
        </div>
        
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span className={currentStep >= 1 ? 'text-green-600 font-medium' : ''}>Add paychecks</span>
          <span className={currentStep >= 2 ? 'text-green-600 font-medium' : ''}>Fill pay periods</span>
          <span className={currentStep >= 3 ? 'text-green-600 font-medium' : ''}>Ready!</span>
        </div>
      </div>

      {/* Your Paychecks Section (formerly Templates) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">💰 Your Paychecks</h2>
              <p className="text-gray-500 text-sm mt-1">
                Add your regular income sources — they&apos;ll auto-fill each pay period
              </p>
            </div>
            <button
              onClick={() => {
                setShowPaycheckForm(!showPaycheckForm);
                setEditingPaycheck(null);
                setNewPaycheck({ name: '', default_amount: '' });
              }}
              data-testid="add-template-button"
              className="px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors text-sm"
            >
              + Add Paycheck
            </button>
          </div>

          {activePaychecks.length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <span className="text-gray-500">Expected per pay period:</span>
              <span className="font-bold text-green-600 text-lg">${totalActivePaychecks.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Add Paycheck Form */}
        {showPaycheckForm && (
          <div className="p-6 bg-gray-50 border-b border-gray-100">
            <p className="font-medium text-gray-700 mb-4">
              {editingPaycheck ? 'Edit paycheck' : 'New paycheck'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  What do you call this income? *
                </label>
                <input
                  type="text"
                  value={newPaycheck.name}
                  onChange={(e) => setNewPaycheck({ ...newPaycheck, name: e.target.value })}
                  placeholder="e.g., Main Job, Side Hustle, Freelance"
                  data-testid="template-name-input"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Amount per paycheck ($) *
                </label>
                <input
                  type="number"
                  value={newPaycheck.default_amount}
                  onChange={(e) => setNewPaycheck({ ...newPaycheck, default_amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="template-amount-input"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={savePaycheck}
                  disabled={saving || !newPaycheck.name || !newPaycheck.default_amount}
                  data-testid="save-template-button"
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingPaycheck ? 'Update Paycheck' : 'Save Paycheck'}
                </button>
                <button
                  onClick={() => {
                    setShowPaycheckForm(false);
                    setEditingPaycheck(null);
                    setNewPaycheck({ name: '', default_amount: '' });
                  }}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Paychecks List */}
        <div className="p-6">
          {templates.length > 0 ? (
            <div className="space-y-3">
              {templates.map((paycheck) => (
                <PaycheckCard
                  key={paycheck.id}
                  paycheck={paycheck}
                  onEdit={editPaycheck}
                  onDelete={deletePaycheck}
                  onToggle={togglePaycheckActive}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">💼</div>
              <p className="text-gray-600 font-medium">No paychecks set up yet</p>
              <p className="text-gray-400 text-sm mt-1">
                Add your salary, side income, or any regular payments you receive
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Settings Card - Compact */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-bold text-gray-900">⚙️ Pay Schedule</h2>
          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
            {savingSettings ? 'Saving...' : '✓ Auto-saved'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">How often do you get paid?</label>
            <select
              value={payFrequency}
              onChange={(e) => handlePayFrequencyChange(e.target.value)}
              data-testid="income-pay-frequency-select"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="monthly">Monthly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">When&apos;s your next payday?</label>
            <input
              type="date"
              value={format(payDate, 'yyyy-MM-dd')}
              onChange={(e) => {
                const selectedDate = new Date(e.target.value + 'T12:00:00');
                handlePayDateChange(selectedDate);
              }}
              data-testid="income-pay-date-input"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl w-full">
              <input
                type="checkbox"
                checked={forceThree}
                onChange={(e) => handleForceThreeChange(e.target.checked)}
                data-testid="income-force-three-checkbox"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show extra pay period</span>
            </label>
          </div>
        </div>
      </div>

      {/* Pay Period Cards */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">📅 Your Pay Periods</h2>
          <Link to="/dashboard" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            View Dashboard →
          </Link>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {payPeriods.map((period, index) => (
            <PeriodCard
              key={index}
              period={period}
              index={index}
              onAddIncome={addIncomeSource}
              onDeleteIncome={deleteIncomeSource}
              onApplyPaychecks={applyPaychecks}
              activePaychecksCount={activePaychecks.length}
              saving={saving}
              newIncome={newIncome[index] || { name: '', amount: '' }}
              onIncomeChange={handleNewIncomeChange}
              payFrequency={payFrequency}
            />
          ))}
        </div>
      </div>

      {/* Inline Help - Contextual */}
      {templates.length === 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
          <h3 className="font-semibold text-blue-900 mb-2">💡 Quick tip</h3>
          <p className="text-blue-800 text-sm">
            Start by adding your main paycheck above. Once set up, you can auto-fill any pay period with one click — no need to re-enter amounts each time!
          </p>
        </div>
      )}

      {templates.length > 0 && !hasAnyIncome && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-2xl p-6">
          <h3 className="font-semibold text-green-900 mb-2">✨ You&apos;re almost there!</h3>
          <p className="text-green-800 text-sm">
            Your paychecks are set up. Now click <strong>&quot;Auto-fill from Paychecks&quot;</strong> on any pay period above to add your income. The Dashboard will update automatically!
          </p>
        </div>
      )}

      {/* Month Summary Modal */}
      {showMonthSummary && monthSummary && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-3xl max-w-md w-full p-1 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-white rounded-[22px] p-6 space-y-5">
              {/* Confetti Header */}
              <div className="text-center">
                <div className="text-6xl mb-3">🎉</div>
                <h2 className="text-2xl font-black text-gray-900">Month Complete!</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Saved as <span className="font-semibold text-emerald-600">{monthSummary.monthName}</span>
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 text-center border border-green-100">
                  <div className="text-2xl font-black text-green-600">
                    ${(monthSummary.total_income || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-green-700 font-medium mt-1">Total Income</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 text-center border border-blue-100">
                  <div className="text-2xl font-black text-blue-600">
                    ${(monthSummary.total_saved || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-700 font-medium mt-1">Net Saved</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 text-center border border-amber-100">
                  <div className="text-2xl font-black text-amber-600">
                    ${(monthSummary.total_bills || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-amber-700 font-medium mt-1">Bills Paid</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 text-center border border-purple-100">
                  <div className="text-2xl font-black text-purple-600">
                    ${(monthSummary.total_expenses || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-purple-700 font-medium mt-1">Expenses</div>
                </div>
              </div>

              {/* Bills Progress */}
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Bills Completed</span>
                  <span className="text-sm font-bold text-emerald-600">
                    {monthSummary.bills_paid || 0}/{monthSummary.total_bills_count || 0}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all"
                    style={{ 
                      width: `${monthSummary.total_bills_count > 0 
                        ? (monthSummary.bills_paid / monthSummary.total_bills_count) * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </div>

              {/* Streak Status */}
              {monthSummary.streak_days > 0 && (
                <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-2xl p-4 text-white text-center">
                  <div className="text-3xl mb-1">🔥</div>
                  <div className="text-xl font-black">{monthSummary.streak_days} Day Streak!</div>
                  <div className="text-yellow-100 text-sm font-medium">{monthSummary.streak_title}</div>
                </div>
              )}

              {/* Update Bills/Debts Prompt */}
              {showUpdatePrompt && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4">
                  <div className="text-center mb-3">
                    <div className="text-2xl mb-1">📋</div>
                    <div className="font-bold text-blue-900">New Month, New Updates?</div>
                    <div className="text-blue-700 text-sm mt-1">
                      Do you need to update any bills or debts for this month?
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowMonthSummary(false);
                        setShowUpdatePrompt(false);
                        navigate('/app/bills');
                      }}
                      className="flex-1 py-2 bg-blue-500 text-white rounded-xl font-semibold text-sm hover:bg-blue-600 transition-all"
                      data-testid="update-bills-btn"
                    >
                      Update Bills
                    </button>
                    <button
                      onClick={() => {
                        setShowMonthSummary(false);
                        setShowUpdatePrompt(false);
                        navigate('/app/debt-snowball');
                      }}
                      className="flex-1 py-2 bg-indigo-500 text-white rounded-xl font-semibold text-sm hover:bg-indigo-600 transition-all"
                      data-testid="update-debts-btn"
                    >
                      Update Debts
                    </button>
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMonthSummary(false);
                    setShowUpdatePrompt(false);
                  }}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-200"
                >
                  Start Fresh! 🚀
                </button>
              </div>

              <p className="text-center text-xs text-gray-400">
                View past months in <span className="font-semibold">History</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal for 1099 Premium Feature */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="1099_tax_tracking"
      />

      {/* Money Rain Animation */}
      {showMoneyRain && (
        <MoneyRain 
          duration={3000} 
          amount={35} 
          onComplete={() => setShowMoneyRain(false)} 
        />
      )}
    </div>
  );
}

export default Income;
